import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Ban, RefreshCw, ShieldCheck, Store, Users } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDateTime, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MessagingContactDto, WhatsappStatus } from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Pagination,
  PromptModal,
  SearchInput,
  Select,
  Table,
  TableContainer,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import { useDebouncedValue } from '../../utils/useDebouncedValue';
import DataState from '../DataState';
import {
  BLOCK_SOURCE_LABEL,
  PARTNER_BADGE_LABEL,
  WHATSAPP_STATUS_LABEL,
  WHATSAPP_STATUS_TONE,
  isUndeliverable,
} from './messagingLabels';

const Explainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;

  > *:first-child {
    flex: 1;
    min-width: 220px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

/**
 * Linha em evidência para quem não deve receber.
 *
 * O fundo vai no `td` e não no `tr` porque a `Table` pinta o hover na célula - sem
 * isso, passar o mouse apagava justamente o destaque que a spec pede.
 */
const Row = styled.tr<{ $blocked: boolean }>`
  ${(p) =>
    p.$blocked &&
    css`
      td {
        background: ${p.theme.error}0f;
      }

      &:hover td {
        background: ${p.theme.error}1f;
      }
    `}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 190px;
  padding: 0.15rem 0.5rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  font-size: 0.72rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Loja do catálogo vinculada ao número.
 *
 * Discreta de propósito: estar no catálogo não impede nada. O vermelho continua reservado
 * a bloqueado e sem WhatsApp; aqui só se diz de quem é o número.
 */
const MerchantLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.textMuted};
  }
`;

const MerchantName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const Danger = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.error};
`;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const BLOCKED_FILTER = [
  { value: '', label: 'Bloqueio: todos' },
  { value: 'true', label: 'Somente bloqueados' },
  { value: 'false', label: 'Somente liberados' },
];

/**
 * Parceiro é `partnershipStatus === 'ACTIVE'`, não "tem loja no catálogo".
 *
 * Por isso o filtro fala de parceria e não de loja: quem está só catalogado continua sendo
 * público de prospecção e precisa continuar aparecendo em "não parceiros".
 */
const PARTNER_FILTER = [
  { value: '', label: 'Parceria: todos' },
  { value: 'true', label: 'Somente parceiros' },
  { value: 'false', label: 'Somente não parceiros' },
];

const WHATSAPP_FILTER = [
  { value: '', label: 'WhatsApp: todos' },
  { value: 'VALID', label: 'Com WhatsApp' },
  { value: 'INVALID', label: 'Sem WhatsApp' },
  { value: 'UNKNOWN', label: 'Ainda verificando' },
];

/** Alvo de uma ação que exige motivo - o mesmo modal serve para bloquear e liberar. */
type ReasonAction = { contact: MessagingContactDto; mode: 'block' | 'unblock' };

interface ContactsTabProps {
  canManage: boolean;
}

/**
 * Panorama por número: onde ele está, se pode receber e por que não.
 *
 * A lista mostra o contato; esta aba mostra o **número**, que é o que o WhatsApp
 * bloqueia. Um lojista em três listas é uma linha só aqui, e é aqui que se descobre
 * que ele já ia receber a mesma campanha três vezes.
 */
const ContactsTab: React.FC<ContactsTabProps> = ({ canManage }) => {
  const { showNotification } = useNotification();

  const [search, setSearch] = useState('');
  const [blockedFilter, setBlockedFilter] = useState('');
  const [whatsappFilter, setWhatsappFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const debouncedSearch = useDebouncedValue(search.trim());

  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [savingReason, setSavingReason] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState<string | null>(null);

  const contacts = useAsync(
    () =>
      backofficeApi.listMessagingContacts({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        blocked: blockedFilter === '' ? undefined : blockedFilter === 'true',
        whatsappStatus: (whatsappFilter || undefined) as WhatsappStatus | undefined,
        partner: partnerFilter === '' ? undefined : partnerFilter === 'true',
      }),
    [page, pageSize, debouncedSearch, blockedFilter, whatsappFilter, partnerFilter]
  );

  const page$ = contacts.data;
  const rows = page$?.content ?? [];

  const confirmReason = async (reason: string): Promise<void> => {
    if (!reasonAction) return;
    const { contact, mode } = reasonAction;
    setSavingReason(true);
    try {
      if (mode === 'block') {
        await backofficeApi.blockMessagingContact(contact.phoneNumber, { reason });
        showNotification(
          'Número bloqueado. Os envios pendentes dele foram descartados.',
          'success'
        );
      } else {
        await backofficeApi.unblockMessagingContact(contact.phoneNumber, { reason });
        // Liberar não reenfileira: quem quer tentar de novo usa o "Reenviar falhas".
        showNotification(
          'Número liberado. Ele volta a entrar em campanhas novas, mas nada é reenviado sozinho.',
          'success'
        );
      }
      setReasonAction(null);
      contacts.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o bloqueio.'), 'error');
    } finally {
      setSavingReason(false);
    }
  };

  const checkWhatsapp = async (contact: MessagingContactDto): Promise<void> => {
    setCheckingPhone(contact.phoneNumber);
    try {
      const report = await backofficeApi.checkMessagingContactsWhatsapp({
        phoneNumbers: [contact.phoneNumber],
      });
      showNotification(
        report.unverified > 0
          ? 'O provedor não respondeu sobre este número - o status continua como estava.'
          : `Verificado: ${report.valid} com WhatsApp, ${report.invalid} sem.`,
        report.unverified > 0 ? 'error' : 'success'
      );
      contacts.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível verificar o número.'), 'error');
    } finally {
      setCheckingPhone(null);
    }
  };

  return (
    <>
      <Explainer>
        <Users size={18} />
        <span>
          Um registro por número, valendo para <strong>todas</strong> as listas. Bloqueado não
          recebe: os envios pendentes dele são descartados e campanhas novas já o pulam. Número{' '}
          <strong>sem WhatsApp</strong> é bloqueado sozinho - insistir nele é o que derruba o número
          comercial. Quando o número é o de uma loja do catálogo, a loja aparece na linha; o selo{' '}
          <strong>Parceiro</strong> marca só quem já tem parceria ativa - os demais continuam sendo
          prospecção.
        </span>
      </Explainer>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por WhatsApp, nome ou empresa..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <Select
          options={BLOCKED_FILTER}
          value={blockedFilter}
          onChange={(e) => {
            setBlockedFilter(e.target.value);
            setPage(0);
          }}
        />
        <Select
          options={WHATSAPP_FILTER}
          value={whatsappFilter}
          onChange={(e) => {
            setWhatsappFilter(e.target.value);
            setPage(0);
          }}
        />
        <Select
          options={PARTNER_FILTER}
          value={partnerFilter}
          onChange={(e) => {
            setPartnerFilter(e.target.value);
            setPage(0);
          }}
        />
        <Button variant="ghost" onClick={contacts.reload} disabled={contacts.loading}>
          <RefreshCw size={16} /> Atualizar
        </Button>
      </Toolbar>

      <DataState
        // `&& !page$`: sem isso, cada tecla da busca trocaria a tabela por esqueletos.
        loading={contacts.loading && !page$}
        error={contacts.error}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhum contato encontrado"
        emptyDescription="Os contatos aparecem aqui assim que uma lista é importada."
        onRetry={contacts.reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>WhatsApp</th>
                <th>Contato</th>
                <th>Listas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <Row key={contact.id} $blocked={isUndeliverable(contact)}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <strong>{contact.phoneNumberFormatted}</strong>
                    {contact.lastFailureReason && (
                      <Danger title={contact.lastFailureReason}>
                        Última falha em {formatDateTime(contact.lastFailureAt)}
                      </Danger>
                    )}
                  </td>
                  <td>
                    <strong>{contact.name || '-'}</strong>
                    {contact.company && <FieldHint>{contact.company}</FieldHint>}
                    {contact.merchantName && (
                      <MerchantLine>
                        <Store size={13} />
                        <MerchantName title={contact.merchantName}>
                          {contact.merchantName}
                        </MerchantName>
                        {contact.partner && <Badge tone="primary">{PARTNER_BADGE_LABEL}</Badge>}
                      </MerchantLine>
                    )}
                  </td>
                  <td>
                    {formatNumber(contact.listsCount)} lista(s)
                    {contact.lists.length > 0 && (
                      <Chips>
                        {contact.lists.map((list) => (
                          <Chip key={list.id} title={list.name}>
                            {list.name}
                          </Chip>
                        ))}
                      </Chips>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Stack>
                      <Badge tone={WHATSAPP_STATUS_TONE[contact.whatsappStatus]}>
                        {WHATSAPP_STATUS_LABEL[contact.whatsappStatus]}
                      </Badge>
                      {contact.blocked && (
                        <Badge tone="danger">
                          Bloqueado
                          {contact.blockSource ? ` · ${BLOCK_SOURCE_LABEL[contact.blockSource]}` : ''}
                        </Badge>
                      )}
                      {contact.optedOut && <Badge tone="warning">Opt-out</Badge>}
                      {contact.blocked && contact.blockReason && (
                        <FieldHint title={`Bloqueado em ${formatDateTime(contact.blockedAt)}`}>
                          {contact.blockReason}
                        </FieldHint>
                      )}
                    </Stack>
                  </td>
                  <td>
                    <RowActions>
                      {canManage && (
                        <>
                          <ActionButton
                            title="Verificar WhatsApp"
                            aria-label="Verificar WhatsApp"
                            disabled={checkingPhone === contact.phoneNumber}
                            onClick={() => checkWhatsapp(contact)}
                          >
                            <RefreshCw size={16} />
                          </ActionButton>
                          {contact.blocked ? (
                            <ActionButton
                              title="Liberar número"
                              aria-label="Liberar número"
                              onClick={() => setReasonAction({ contact, mode: 'unblock' })}
                            >
                              <ShieldCheck size={16} />
                            </ActionButton>
                          ) : (
                            <ActionButton
                              title="Bloquear número"
                              aria-label="Bloquear número"
                              color="#DC2626"
                              onClick={() => setReasonAction({ contact, mode: 'block' })}
                            >
                              <Ban size={16} />
                            </ActionButton>
                          )}
                        </>
                      )}
                    </RowActions>
                  </td>
                </Row>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {(page$?.totalPages ?? 0) > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={page$?.totalPages ?? 1}
          totalItems={page$?.totalElements}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={contacts.loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}

      <PromptModal
        isOpen={reasonAction !== null}
        title={reasonAction?.mode === 'block' ? 'Bloquear número' : 'Liberar número'}
        // O motivo é obrigatório nos dois sentidos: é o que a auditoria guarda e o que
        // o próximo operador lê para não desfazer a decisão de alguém às cegas.
        label="Motivo"
        message={
          reasonAction?.mode === 'block' ? (
            <>
              <strong>{reasonAction?.contact.phoneNumberFormatted}</strong> deixa de receber em todas
              as listas e campanhas. Os envios pendentes dele viram descartados na hora.
            </>
          ) : (
            <>
              <strong>{reasonAction?.contact.phoneNumberFormatted}</strong> volta a entrar em
              campanhas novas. Nada é reenviado automaticamente - para tentar de novo, use o
              &quot;Reenviar falhas&quot; da campanha.
            </>
          )
        }
        placeholder={
          reasonAction?.mode === 'block'
            ? 'Ex.: número respondeu que é engano'
            : 'Ex.: lojista confirmou o número novo'
        }
        confirmLabel={reasonAction?.mode === 'block' ? 'Bloquear' : 'Liberar'}
        danger={reasonAction?.mode === 'block'}
        loading={savingReason}
        onConfirm={confirmReason}
        onClose={() => setReasonAction(null)}
      />
    </>
  );
};

export default ContactsTab;
